import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Search, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertCircle, 
  Calendar, 
  MapPin, 
  User, 
  Briefcase,
  FileText,
  Copy,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { getStatusBadge, formatUzPhone } from '../../utils/helpers';
import { showToast } from '../../components/common/Toast';

export default function CheckStatusPage() {
  const { t, localized, currentLang } = useLanguage();
  const [searchParams] = useSearchParams();

  const [bookingRef, setBookingRef] = useState('');
  const [contact, setContact] = useState('');
  const [loading, setLoading] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const paramRef = searchParams.get('ref') || '';
    const paramContact = searchParams.get('contact') || '';
    if (paramRef || paramContact) {
      if (paramRef) setBookingRef(paramRef);
      if (paramContact) setContact(paramContact);
      executeLookup(paramRef, paramContact);
    }
  }, [searchParams]);

  const executeLookup = async (refVal, contactVal) => {
    setLoading(true);
    setSearched(true);
    setAppointment(null);

    try {
      const res = await api.post('/public/appointments/lookup', {
        bookingReference: refVal?.trim() || undefined,
        contact: contactVal?.trim() || undefined,
      });
      if (res?.success && res?.appointment) {
        setAppointment(res.appointment);
      } else {
        showToast(res?.message || 'Ariza topilmadi', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Ariza topilmadi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!bookingRef.trim() && !contact.trim()) {
      showToast('Bron raqami yoki telefon raqamingizdan kamida birini kiriting', 'error');
      return;
    }
    executeLookup(bookingRef, contact);
  };

  const statusBadge = appointment ? getStatusBadge(appointment.status) : null;

  return (
    <div className="bg-[#05080e] min-h-screen py-10 sm:py-14 text-slate-200">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Back Navigation Bar - Fully Responsive on all screen sizes */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8 pb-4 border-b border-[#141d2d]">
          <Link
            to="/"
            className="inline-flex items-center justify-center sm:justify-start space-x-2 text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:text-amber-200 transition-all py-2.5 px-4 rounded-lg bg-[#080d17] border border-[#1b2b46] hover:border-[#c59b27]/60 cursor-pointer shadow-sm group"
          >
            <ArrowLeft className="w-4 h-4 text-[#c59b27] group-hover:-translate-x-1 transition-transform shrink-0" />
            <span className="truncate">{t('back_to_home', 'Bosh sahifaga qaytish')}</span>
          </Link>

          <Link
            to="/book"
            className="inline-flex items-center justify-center sm:justify-end space-x-2 text-xs font-semibold uppercase tracking-wider text-slate-300 hover:text-[#c59b27] transition-all py-2.5 px-4 rounded-lg bg-[#080d17]/80 hover:bg-[#0e1726] border border-[#1b2b46] hover:border-[#c59b27]/60 cursor-pointer shadow-sm group"
          >
            <span className="truncate">{t('new_booking', 'Yangi qabulga yozilish')}</span>
            <ArrowRight className="w-4 h-4 text-[#c59b27] group-hover:translate-x-1 transition-transform shrink-0" />
          </Link>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
            {t('client_portal_tag', 'MIJOZLAR PORTALI')}
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100 mb-2">
            {t('lookup_title', 'Ariza Holatini Tekshirish')}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm font-light">
            {t('lookup_subtitle', 'Bron raqamingiz va telefoningiz orqali uchrashuv holati va ma’muriyat eslatmasini bilib oling.')}
          </p>
        </div>

        {/* Search Form Card */}
        <div className="bg-[#090e18] rounded-xl border border-[#1b2b46] p-6 sm:p-8 shadow-2xl shadow-black/80 mb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  {t('booking_id_label', 'Bron Raqami (Booking ID)')}
                </label>
                <input
                  type="text"
                  placeholder={t('lookup_ref_placeholder', 'Masalan: APT-2026-000001')}
                  value={bookingRef}
                  onChange={(e) => setBookingRef(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-[#060a12] border border-[#1b2b46] rounded text-sm text-slate-100 font-mono uppercase focus:outline-none focus:border-[#c59b27] transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  {t('phone_label', 'Telefon raqami')}
                </label>
                <input
                  type="text"
                  placeholder={t('lookup_contact_placeholder', '+998 (97) 325-07-70')}
                  value={contact}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.includes('@') || /^[a-zA-Z]/.test(val)) {
                      setContact(val);
                    } else {
                      setContact(formatUzPhone(val, contact));
                    }
                  }}
                  className="w-full px-3.5 py-2.5 bg-[#060a12] border border-[#1b2b46] rounded text-sm text-slate-100 font-mono focus:outline-none focus:border-[#c59b27] transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded bg-[#b38038] hover:bg-[#c59b27] text-white text-xs font-bold uppercase tracking-wider shadow-lg transition-all flex items-center justify-center cursor-pointer disabled:opacity-50 mt-2"
            >
              {loading ? (
                t('searching', 'Qidirilmoqda...')
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  {t('check_status_btn_text', 'Holatni tekshirish')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Results Card */}
        {appointment && (
          <div className="bg-[#090e18] rounded-xl border border-[#c59b27]/40 p-6 sm:p-8 shadow-2xl shadow-black/80 space-y-6 animate-in zoom-in-95">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-[#141d2d] gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block">Bron Raqami</span>
                <span className="font-mono text-xl font-bold text-[#c59b27]">{appointment.bookingReference}</span>
              </div>
              <div>
                {statusBadge && (
                  <span className={`px-3 py-1 rounded text-xs font-semibold border ${statusBadge.classes || statusBadge.className}`}>
                    {statusBadge.label}
                  </span>
                )}
              </div>
            </div>

            {/* MANDATORY ADMIN NOTE (IF CONFIRMED OR REJECTED) */}
            {appointment.adminNote && (
              <div className="p-4 rounded-lg bg-[#0e1728] border border-[#c59b27]/40 space-y-1">
                <span className="text-xs uppercase font-bold text-[#c59b27] flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  {currentLang === 'uz' ? 'Ma’muriyat Rasmiy Eslatmasi:' : currentLang === 'ru' ? 'Официальное примечание администрации:' : 'Official Admin Note:'}
                </span>
                <p className="text-slate-200 text-sm leading-relaxed font-light">
                  "{appointment.adminNote}"
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="p-3 bg-[#060a12] rounded border border-[#141d2d]">
                <span className="text-slate-500 block mb-1">
                  {currentLang === 'uz' ? 'Mijoz Ismi:' : currentLang === 'ru' ? 'Имя клиента:' : 'Client Name:'}
                </span>
                <span className="text-slate-200 font-medium text-sm">{appointment.clientName}</span>
              </div>

              <div className="p-3 bg-[#060a12] rounded border border-[#141d2d]">
                <span className="text-slate-500 block mb-1">
                  {currentLang === 'uz' ? 'Xizmat Yo‘nalishi:' : currentLang === 'ru' ? 'Направление услуги:' : 'Service:'}
                </span>
                <span className="text-slate-200 font-medium text-sm">
                  {localized(appointment.service, 'title') || 'Yuridik konsultatsiya'}
                </span>
              </div>

              <div className="p-3 bg-[#060a12] rounded border border-[#141d2d]">
                <span className="text-slate-500 block mb-1">
                  {currentLang === 'uz' ? 'Belgilangan Advokat:' : currentLang === 'ru' ? 'Назначенный адвокат:' : 'Assigned Attorney:'}
                </span>
                <span className="text-slate-200 font-medium text-sm">{appointment.lawyer?.fullName}</span>
              </div>

              <div className="p-3 bg-[#060a12] rounded border border-[#141d2d]">
                <span className="text-slate-500 block mb-1">
                  {currentLang === 'uz' ? 'Uchrashuv Vaqti:' : currentLang === 'ru' ? 'Время встречи:' : 'Appointment Time:'}
                </span>
                <span className="text-slate-200 font-medium text-sm">
                  {appointment.appointmentDate} ({appointment.startTime})
                </span>
              </div>
            </div>

            <div className="p-3 bg-[#060a12] rounded border border-[#141d2d] text-xs">
              <span className="text-slate-500 block mb-1">
                {currentLang === 'uz' ? 'Ofis Manzili:' : currentLang === 'ru' ? 'Адрес офиса:' : 'Office Address:'}
              </span>
              <span className="text-slate-200">
                {localized(appointment.office, 'address') || appointment.office?.name}
              </span>
            </div>
          </div>
        )}

        {searched && !appointment && !loading && (
          <div className="text-center py-12 bg-[#090e18] rounded-xl border border-rose-900/40 p-6">
            <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
            <h3 className="font-serif text-lg font-bold text-slate-200 mb-1">
              {currentLang === 'uz' ? 'Ariza Topilmadi' : currentLang === 'ru' ? 'Заявка не найдена' : 'Application Not Found'}
            </h3>
            <p className="text-slate-400 text-xs max-w-md mx-auto">
              {currentLang === 'uz'
                ? 'Kiritilgan ma’lumotlar bo‘yicha ariza topilmadi. Bron raqami (masalan: APT-2026-000001) va telefon raqamingiz to‘g‘ri kiritilganligini tekshiring.'
                : currentLang === 'ru'
                ? 'Заявка по указанным данным не найдена. Проверьте номер брони (например: APT-2026-000001) и номер телефона.'
                : 'No application found with the provided details. Please verify your booking reference and phone number.'}
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
